import { useEffect, useLayoutEffect, useRef } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { PilotTurn } from "../../../lib/callPilot";

/** Within this many pixels of the bottom counts as "following along". */
const STICK_PX = 48;

/**
 * The call as a conversation: the operator's side on the right, the caller's on
 * the left, the way any chat reads.
 *
 * The scroll rule is the whole trick. New lines arrive every few seconds, and a
 * reader who has scrolled up to re-read something the caller said two minutes
 * ago must not be yanked back down by the next one - that turns the panel from
 * a record into a thing that fights you. So: follow the bottom only while the
 * reader is already at the bottom.
 */
export function TranscriptList({ turns, pending }: { turns: PilotTurn[]; pending: number }) {
  const t = useT();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const stuckRef = useRef(true);

  const onScroll = () => {
    const box = boxRef.current;
    if (!box) return;
    stuckRef.current = box.scrollHeight - box.scrollTop - box.clientHeight <= STICK_PX;
  };

  // Layout effect, not effect: scrolling after the browser has painted the new
  // line is a visible jump, and this runs before it.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || !stuckRef.current) return;
    box.scrollTop = box.scrollHeight;
  }, [turns.length, pending]);

  // A panel opened mid-call starts at the end of the conversation, not at the
  // beginning of it. After a frame, because the two columns settle their heights
  // in the same pass and scrolling to a height that is about to change lands
  // somewhere in the middle.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const box = boxRef.current;
      if (box) box.scrollTop = box.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    // No `flex-1` here: it is `flex: 1 1 0%`, and being a utility it wins over
    // the `flex: 1 1 55%` in `.crm-cp-scroll`, which collapses the split this
    // column and the suggestions are supposed to share.
    <div ref={boxRef} onScroll={onScroll} className="crm-cp-scroll min-h-0 overflow-y-auto px-4 py-3">
      {turns.length === 0 ? (
        <p className="px-1 py-6 text-center text-[12.5px] leading-[1.6] text-muted-foreground">
          {t("calls.pilot.transcriptEmpty")}
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {turns.map((turn, index) => (
            <li
              key={turn.id ?? `${turn.seq}-${index}`}
              // Three branches, not a boolean. `is-me` alone would leave an
              // abstention wearing the DEFAULT bubble, and the default bubble is
              // the caller's - so "we could not tell" would read as "the
              // customer said this", which is the exact error being fixed.
              className={cn(
                "crm-cp-turn",
                turn.speaker === "operator" && "is-me",
                turn.speaker === "unknown" && "is-nobody",
              )}
            >
              <span className="crm-cp-who">
                {t(
                  turn.speaker === "operator"
                    ? "calls.pilot.operator"
                    : turn.speaker === "unknown"
                      ? "calls.pilot.unknownSpeaker"
                      : "calls.pilot.client",
                )}
              </span>
              <p className="crm-cp-said">{turn.text}</p>
            </li>
          ))}
        </ol>
      )}

      {pending > 0 ? (
        <div className="crm-cp-wait" aria-live="polite">
          <i />
          <i />
          <i />
          <span className="sr-only">{t("calls.pilot.listening")}</span>
        </div>
      ) : null}
    </div>
  );
}
